<div class="blackout-rule-form" [attr.data-testid]="'blackout-rule-form-' + index">

  <p class="blackout-hint" *ngIf="timezone">
    Times below are in <strong>{{ timezone }}</strong>.
  </p>

  <div class="blackout-field blackout-rule-name-field">
    <label class="label is-small" [attr.for]="'blackout-rule-' + index + '-name-input'">Rule name <span class="blackout-optional">(optional)</span></label>
    <input
      class="input is-small blackout-rule-name-input"
      type="text"
      [disabled]="disabled"
      [ngModel]="range.name || ''"
      (ngModelChange)="setRuleName($event)"
      placeholder="e.g. Weeknight blackout"
      maxlength="255"
      [id]="'blackout-rule-' + index + '-name-input'"
      [attr.data-testid]="'blackout-rule-' + index + '-name'"
    />
  </div>

  <div class="blackout-field">
    <label class="label is-small" [id]="'blackout-rule-' + index + '-repeats-label'">Repeats on</label>
    <div class="blackout-dow" role="group" [attr.aria-labelledby]="'blackout-rule-' + index + '-repeats-label'">
      <label *ngFor="let dow of BLACKOUT_DOW_PROPS" class="checkbox" [attr.title]="dow.caption">
        <input
          type="checkbox"
          [disabled]="disabled"
          [ngModel]="isDowChecked(dow.prop)"
          (ngModelChange)="setDowChecked(dow.prop, $event)"
          [attr.data-testid]="'blackout-rule-' + index + '-' + dow.prop"
        />
        {{ dow.short }}
      </label>
    </div>
    <div class="buttons are-small blackout-presets">
      <button type="button" class="button is-small is-light" [disabled]="disabled" (click)="setWeekdayPreset('weekdays')">Mon–Fri</button>
      <button type="button" class="button is-small is-light" [disabled]="disabled" (click)="setWeekdayPreset('weekend')">Sat–Sun</button>
      <button type="button" class="button is-small is-light" [disabled]="disabled" (click)="setWeekdayPreset('all')">Every day</button>
      <button type="button" class="button is-small is-light" [disabled]="disabled" (click)="setWeekdayPreset('none')">Clear</button>
    </div>
  </div>

  <div class="blackout-field blackout-dates-field">
    <ng-container *ngIf="dateSectionOpen || hasDateValues(); else datesCollapsed">
      <label class="label is-small" [id]="'blackout-rule-' + index + '-dates-label'">Date range <span class="blackout-optional">(optional)</span></label>
      <div class="blackout-date-row" role="group" [attr.aria-labelledby]="'blackout-rule-' + index + '-dates-label'">
        <div class="blackout-date-field">
          <span class="blackout-sub-label">Start date</span>
          <blackout-date-dropdown
            [value]="range.date_start"
            [disabled]="disabled"
            [testIdPrefix]="'blackout-rule-' + index + '-date-start'"
            ariaLabel="Start date"
            (valueChange)="onDateStartChange($event)"
          ></blackout-date-dropdown>
        </div>
        <div class="blackout-date-field">
          <span class="blackout-sub-label">End date</span>
          <blackout-date-dropdown
            [value]="range.date_end"
            [disabled]="disabled"
            [testIdPrefix]="'blackout-rule-' + index + '-date-end'"
            ariaLabel="End date"
            (valueChange)="onDateEndChange($event)"
          ></blackout-date-dropdown>
        </div>
      </div>
      <div class="buttons are-small">
        <button type="button" class="button is-small is-light" [disabled]="disabled" *ngIf="hasDateValues()" (click)="clearDates()">Clear dates</button>
        <button type="button" class="button is-small is-text" [disabled]="disabled" *ngIf="!hasDateValues()" (click)="hideDateSection()">Hide dates</button>
      </div>
    </ng-container>
    <ng-template #datesCollapsed>
      <button type="button" class="button is-small is-light" [disabled]="disabled" (click)="openDateSection()">+ Limit to specific dates</button>
    </ng-template>
  </div>

  <div class="blackout-field">
    <label class="label is-small" [id]="'blackout-rule-' + index + '-hours-label'">Hours</label>

    <label class="checkbox blackout-all-day-toggle">
      <input
        type="checkbox"
        [disabled]="disabled"
        [ngModel]="isAllDayMode()"
        (ngModelChange)="setAllDayMode($event)"
        [attr.data-testid]="'blackout-rule-' + index + '-all-day'"
      />
      All day
    </label>

    <p class="blackout-hint" *ngIf="isAllDayMode()">
      Non-working for the full day on matching days or dates.
    </p>

    <div class="blackout-time-row" *ngIf="!isAllDayMode()" role="group" [attr.aria-labelledby]="'blackout-rule-' + index + '-hours-label'">
      <div class="blackout-time-field">
        <span class="blackout-sub-label">From</span>
        <blackout-time-dropdown
          [value]="range.time_start"
          [disabled]="disabled"
          [testIdPrefix]="'blackout-rule-' + index + '-time-start'"
            ariaLabel="From"
          (valueChange)="onTimeChange('time_start', $event)"
        ></blackout-time-dropdown>
      </div>
      <div class="blackout-time-field">
        <span class="blackout-sub-label">Until</span>
        <blackout-time-dropdown
          [value]="range.time_end"
          [disabled]="disabled"
          [testIdPrefix]="'blackout-rule-' + index + '-time-end'"
            ariaLabel="Until"
          (valueChange)="onTimeChange('time_end', $event)"
        ></blackout-time-dropdown>
      </div>
    </div>

    <p class="blackout-hint" *ngIf="!isAllDayMode()">
      Times use 24-hour clock in 15-minute steps. An end time earlier than the start time runs overnight.
    </p>
  </div>

  <div class="blackout-field blackout-scope-field">
    <ng-container *ngIf="scopeSectionOpen || hasScopeValues(); else scopeCollapsed">
      <label class="label is-small">Pause scope <span class="blackout-optional">(optional)</span></label>
      <p class="blackout-hint is-size-7">
        Limit this rule to selected batch policies, assessment items, or scorers. Leave all unchecked to apply to everyone on the window.
      </p>
      <div class="blackout-scope-group" *ngIf="scopePickerOptions?.batch_alloc_policies?.length">
        <span class="blackout-sub-label">Batch allocation policies</span>
        <div class="blackout-scope-list" role="group" aria-label="Batch allocation policies">
          <label class="checkbox" *ngFor="let row of scopePickerOptions.batch_alloc_policies">
            <input
              type="checkbox"
              [disabled]="disabled"
              [checked]="isScopeChecked('scope_batch_alloc_policy_ids', row.id)"
              (change)="toggleScope('scope_batch_alloc_policy_ids', row.id)"
            />
            {{ policyLabel(row) }}
          </label>
        </div>
      </div>
      <div class="blackout-scope-group" *ngIf="scopePickerOptions?.marking_window_items?.length">
        <span class="blackout-sub-label">Assessment items</span>
        <div class="blackout-scope-list" role="group" aria-label="Assessment items">
          <label class="checkbox" *ngFor="let row of scopePickerOptions.marking_window_items">
            <input
              type="checkbox"
              [disabled]="disabled"
              [checked]="isScopeChecked('scope_marking_window_item_ids', row.id)"
              (change)="toggleScope('scope_marking_window_item_ids', row.id)"
            />
            {{ itemLabel(row) }}
          </label>
        </div>
      </div>
      <div class="blackout-scope-group" *ngIf="scopePickerOptions?.scorers?.length">
        <span class="blackout-sub-label">Scorers</span>
        <div class="blackout-scope-list" role="group" aria-label="Scorers">
          <label class="checkbox" *ngFor="let row of scopePickerOptions.scorers">
            <input
              type="checkbox"
              [disabled]="disabled"
              [checked]="isScopeChecked('scope_scorer_uids', row.uid)"
              (change)="toggleScope('scope_scorer_uids', row.uid)"
            />
            {{ scorerLabel(row) }}
          </label>
        </div>
      </div>
      <p class="blackout-hint is-size-7" *ngIf="!scopePickerOptions?.batch_alloc_policies?.length && !scopePickerOptions?.marking_window_items?.length && !scopePickerOptions?.scorers?.length">
        No policies, items, or scorers were found for this scoring window yet.
      </p>
      <div class="buttons are-small">
        <button type="button" class="button is-small is-light" [disabled]="disabled" *ngIf="hasScopeValues()" (click)="clearScope()">Clear scope</button>
        <button type="button" class="button is-small is-text" [disabled]="disabled" *ngIf="!hasScopeValues()" (click)="hideScopeSection()">Hide scope</button>
      </div>
    </ng-container>
    <ng-template #scopeCollapsed>
      <button type="button" class="button is-small is-light" [disabled]="disabled" (click)="openScopeSection()">+ Limit to policies, items, or scorers</button>
    </ng-template>
  </div>

  <div class="blackout-field">
    <label class="label is-small" [id]="'blackout-rule-' + index + '-applies-label'">Applies to</label>
    <div class="blackout-tasks" role="group" [attr.aria-labelledby]="'blackout-rule-' + index + '-applies-label'">
      <label class="checkbox" *ngFor="let task of markingTaskOptions">
        <input
          type="checkbox"
          [disabled]="disabled"
          [checked]="isTaskChecked(task.id)"
          (change)="toggleTask(task.id)"
          [attr.data-testid]="'blackout-rule-' + index + '-task-' + task.id"
        />
        {{ task.caption }}
      </label>
    </div>
  </div>

  <p role="alert" class="blackout-error" *ngIf="validateRange(range)" [attr.data-testid]="'blackout-rule-error-' + index">
    {{ validateRange(range) }}
  </p>
</div>
